import React from 'react';
import styled from 'styled-components';
import css from '@styled-system/css';
import Link from '../components/Link';
import { FetchEmployeeResult } from '../types';
import config from '../config';
import { PhoneIcon, EnvelopeIcon, MobileIcon } from './Icons';
import { EmployeeSchema } from './MicroData';
import { Link as Lnk, theme } from '@wco/toolkit';
import appConfig from '../config';
import defaultImage from '../assets/images/employee.jpg';

const DesktopView = styled.span`
  ${css({
    display: ['none', 'block']
  })}
`;

const Email = styled.li`
  display: block;
  margin: 0;
  padding-left: ${props => props.theme.space.large};
  font-size: ${props => props.theme.fontSizes.text.medium};
`;

const MobileView = styled.span`
  ${css({
    display: ['block', 'none']
  })}
`;

const Telno = styled.li`
  font-size: ${props => props.theme.fontSizes.text.medium};
  display: block;
  margin: 0;
  padding-left: ${props => props.theme.space.large};
`;

const MobileNo = styled.li`
  font-size: ${props => props.theme.fontSizes.text.medium};
  display: block;
  margin: 0;
  padding-left: ${props => props.theme.space.large};
`;

const Details = styled.ul`
  display: inline-grid;
  padding: 0;
  li {
    position: relative;
  }
`;

const Header = styled.div`
  position: relative;
  overflow: hidden;
  height: inherit;
`;

const Img = styled.img`
  max-height: 100%;
  max-width: 100%;
  height: auto;
  width: auto;

  display: inline-block;
  vertical-align: middle;
`;
const ImgHolder = styled.div`
  ${css({
    height: ['auto', '8.4375rem'],
    width: ['5.625rem', '8.4375rem']
  })}

  float: right;
  max-height: 8.4375rem;
  text-align: right;
`;

const Head = styled.div`
  ${css({
    height: ['8.4375rem']
  })}

  width: 100%;
  position: relative;
`;

const Title = styled.li`
  margin-bottom: ${props => props.theme.space.medium};
  color: ${props => props.theme.colors.palette.darkGrey};
  font-size: ${props => props.theme.fontSizes.text.medium};
  display: block;
  font-style: italic;
  padding: 0;
  margin-right: ${props => props.theme.space.small};
  word-break: break-word;
`;

const Name = styled.li`
  margin-bottom: ${props => props.theme.space.small};
  color: ${props => props.theme.headlines.color};
  font-family: ${props => props.theme.font};
  font-size: ${props => props.theme.fontSizes.text.medium};
  display: block;
  font-weight: bold;
  line-height: ${props => props.theme.lineHeights.xsmall};
`;

const Card = styled.div`
  padding: ${props => props.theme.space.medium};
  padding-bottom: ${props => props.theme.space.small};
  margin: 0;
  background: ${props =>
    props.theme.name === 'Skjernbank'
      ? props.theme.colors.palette.lightGrey
      : '#fff'};
  border-radius: 3px;
  border: 1px solid rgba(217, 217, 217, 0.8);
  min-height: 14.75rem;
`;

const EmployeeContainer = styled.ul`
  display: inline-grid;
  padding: 0;
`;

const NameSpan = styled.span`
  color: '#424242';
`;

type EmployeeCardProps = {
  employee: FetchEmployeeResult;
  url?: string | undefined;
  nextRouter?: any;
};

export default function EmployeeCard(props: EmployeeCardProps) {
  const { employee, url, nextRouter } = props;
  const bankImageURLParam =
    theme.name === 'Nordjyskebank' ? 'landbobanken' : theme.name;

  return (
    <>
      <Card>
        <Head>
          <ImgHolder>
            {url ? (
              <Link
                to={`${url}/${employee.userId}/${employee.friendlyName}`}
                nextRouter={nextRouter}
              >
                <Img
                  src={`${config.apiBasePath}${employee.url}?bank=${bankImageURLParam}`.toLowerCase()}
                  onError={e => {
                    e.currentTarget.src =
                      appConfig.defaultImgBaseurl + defaultImage;
                  }}
                  loading="lazy"
                />
              </Link>
            ) : (
              <Img
                src={`${config.apiBasePath}${employee.url}?bank=${bankImageURLParam}`.toLowerCase()}
                onError={e => {
                  e.currentTarget.src =
                    appConfig.defaultImgBaseurl + defaultImage;
                }}
                loading="lazy"
                alt={`Billede af ${employee.firstName} ${employee.lastName}`}
              />
            )}
          </ImgHolder>
          <Header>
            <EmployeeContainer>
              <Name>
                {url ? (
                  <Link
                    to={`${url}/${employee.userId}/${employee.friendlyName}`}
                    nextRouter={nextRouter}
                  >
                    {employee.firstName} {employee.lastName}
                  </Link>
                ) : (
                  <NameSpan>
                    {employee.firstName} {employee.lastName}
                  </NameSpan>
                )}
              </Name>
              <Title>{employee.title}</Title>
            </EmployeeContainer>
          </Header>
        </Head>
        <Details>
          {employee.phone && (
            <Telno>
              <PhoneIcon />
              <DesktopView>
                {employee.phone ? employee.phone : '1234 1234'}
              </DesktopView>
              <MobileView>
                <Lnk href={`tel:+45${employee.phone.replace(/\s/g, '')}`}>
                  {employee.phone ? employee.phone : '1234 1234'}
                </Lnk>
              </MobileView>
            </Telno>
          )}
          {appConfig.showMobile && employee.mobileNo && (
            <MobileNo>
              <MobileIcon />
              <DesktopView>{employee.mobileNo}</DesktopView>
              <MobileView>
                <Lnk href={`tel:+45${employee.mobileNo.replace(/\s/g, '')}`}>
                  {employee.mobileNo}
                </Lnk>
              </MobileView>
            </MobileNo>
          )}
          {employee.email && (
            <Email>
              <EnvelopeIcon />
              <Lnk href={`mailto:${employee.email}`}>{employee.email}</Lnk>
            </Email>
          )}
        </Details>
        <EmployeeSchema {...employee} branchUrl={url} />
      </Card>
    </>
  );
}
